import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TankGauge from "../components/tank_gauge";
import { StatusBadge, SignalBars, BatteryLevel } from "../components/tank_widgets";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { toastify } from "../../../../../../utils/toast";
import { useFacilityStore } from "../../../../../../app/z-store/store";
import { getWaterSensorTanksURL, getWaterSensorTanksSummaryURL } from "../../../../../../utils/urls";
import { formatLitres, formatMetres, formatPercent, tankStatus, timeAgo } from "../utils/format";
import { viewTankPath } from "../utils/routes";

const REFRESH_MS = 60000; // sensors report about once a minute

function DashboardTab({ onTabChange }) {
  const navigate = useNavigate();
  const facilityId = useFacilityStore((state) => state.facilityId);
  const [gatewayCount, setGatewayCount] = useState(null);
  const [tanks, setTanks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [, setClock] = useState(0);

  const getTanks = async () => {
    if (!facilityId) return;
    try {
      const [tanksResponse, summaryResponse] = await Promise.all([
        makeRequest2(getWaterSensorTanksURL(facilityId), "GET"),
        makeRequest2(getWaterSensorTanksSummaryURL(facilityId), "GET"),
      ]);
      if (tanksResponse.success) {
        setTanks(tanksResponse.data.data || []);
        setGatewayCount(tanksResponse.data.gatewayCount ?? null);
      } else toastify(tanksResponse.error, "error");
      if (summaryResponse.success) setSummary(summaryResponse.data.data);
      setLastRefreshed(new Date());
    } catch (err) {
      toastify(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Reloads only the dashboard data, not the whole page
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await getTanks();
    setRefreshing(false);
  };

  useEffect(() => {
    setLoading(true);
    setTanks([]);
    setSummary(null);
    getTanks();
    const refresh = setInterval(getTanks, REFRESH_MS);
    const clock = setInterval(() => setClock((n) => n + 1), 30000); // keeps "x min ago" fresh
    return () => {
      clearInterval(refresh);
      clearInterval(clock);
    };
  }, [facilityId]);

  return (
    <>
      <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
        {lastRefreshed && (
          <span className="text-muted small">
            Last refreshed {lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
        )}
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleRefresh} disabled={refreshing || loading}>
          <i className={`ti ti-refresh me-1 ${refreshing ? "ws-spin" : ""}`}></i>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {summary && tanks.length > 0 && (
        <div className="row">
          <SummaryCard icon="ti ti-antenna" label="Tanks online" value={`${summary.onlineTanks} / ${summary.totalTanks}`} />
          <SummaryCard
            icon="ti ti-bell"
            label="Active alerts"
            value={summary.activeAlerts}
            tone={summary.activeAlerts ? "danger" : null}
            onClick={() => onTabChange && onTabChange("alerts")}
          />
          <SummaryCard icon="ti ti-arrow-down" label="Low tanks" value={summary.lowTanks} tone={summary.lowTanks ? "danger" : null} />
          <SummaryCard
            icon="ti ti-droplet"
            label="Water in storage"
            value={formatLitres(summary.totalVolumeL)}
            hint={summary.totalVolumeL == null ? "Set tank capacities to see this" : null}
          />
        </div>
      )}

      {loading ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">Loading…</div>
        </div>
      ) : tanks.length === 0 && gatewayCount === 0 ? (
        <NoGatewayYet onTabChange={onTabChange} />
      ) : tanks.length === 0 ? (
        <NoTanksYet />
      ) : (
        <div className="row">
          {tanks.map((tank) => {
            const status = tankStatus(tank);
            return (
              <div className="col-md-6 col-xl-4" key={tank._id}>
                <div className="card tank-card" role="button" onClick={() => navigate(viewTankPath(tank._id))}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h5 className="mb-0">{tank.name}</h5>
                        <div className="text-muted small">
                          {tank.location || `Slot ${tank.slot}`} · {tank.gatewayId}
                        </div>
                      </div>
                      <StatusBadge status={status} />
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <TankGauge pct={tank.last?.pct} lowPct={tank.lowPct} highPct={tank.highPct} offline={!tank.online} />
                      <div className="flex-grow-1">
                        <div className="tank-percent">{formatPercent(tank.last?.pct)}</div>
                        <div className="text-muted">
                          {formatMetres(tank.last?.levelM)}
                          {tank.last?.volumeL != null && ` · ${formatLitres(tank.last.volumeL)}`}
                        </div>
                        <div className="d-flex gap-3 my-2 small text-secondary">
                          <BatteryLevel pct={tank.last?.battery} low={tank.last?.battery != null && tank.last.battery <= tank.batteryLowPct} />
                          <SignalBars rssi={tank.last?.rssi} />
                        </div>
                        <div className="text-muted small">Updated {timeAgo(tank.last?.ts)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function SummaryCard({ icon, label, value, tone, hint, onClick }) {
  return (
    <div className="col-6 col-md-3">
      <div className={`card summary-card ${onClick ? "clickable" : ""}`} onClick={onClick}>
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 text-muted small mb-1">
            <i className={icon}></i> {label}
          </div>
          <h3 className={`mb-0 ${tone ? `text-${tone}` : ""}`}>{value}</h3>
          {hint && <div className="text-muted small mt-1">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function NoGatewayYet({ onTabChange }) {
  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">No gateway assigned to this facility</h5>
      </div>
      <div className="card-body">
        <p>
          Tanks are grouped by the 4G gateway (USR-DR512) they report through. Assign this facility's gateway to see its tanks here.
        </p>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onTabChange && onTabChange("gateways")}>
          <i className="ti ti-router me-1"></i> Assign a gateway
        </button>
      </div>
    </div>
  );
}

function NoTanksYet() {
  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">No tanks yet</h5>
      </div>
      <div className="card-body">
        <p>Tanks appear here automatically once the first sensor reading arrives.</p>
        <ol className="text-muted mb-0">
          <li>Pair each terminal on the IF485-LoRa-H1 transceiver (Pair Sensor → Polling).</li>
          <li>Wire the transceiver to the USR-DR512: A→A, B→B.</li>
          <li>Point the DR512 at the water level service and check it shows as connected on the Gateways tab.</li>
        </ol>
      </div>
    </div>
  );
}

export default DashboardTab;
